import {
  type ActivityFilter,
  type BulkResult,
  DEAL_SOURCE_LABELS,
  DEAL_SOURCES,
  type DealListItem,
  type DealSort,
  type Paginated,
  type QuickDealResult,
  type StageItem,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { CalendarClock, Columns3, List, MessageCircle, Plus, Search } from 'lucide-react';
import { type DragEvent, useId, useRef, useState } from 'react';
import { CrmNav, EmptyPanel, Money, Pager, StageBadge } from '@/components/crm-bits';
import { DealFormDialog, LostReasonDialog } from '@/components/crm-forms';
import {
  BulkBar,
  BulkNotice,
  type ColumnDef,
  ColumnsMenu,
  type ColumnsState,
  cleanParams,
  countActive,
  DateRange,
  ExportButton,
  FilterField,
  FilterPanel,
  FilterToggle,
  PresetChips,
  SavedFilters,
  SelectBox,
  SortHeader,
  type SortState,
  useColumns,
  useSelection,
} from '@/components/list-tools';
import { Avatar, Button, ErrorBox, Input, PageHeader, Segmented, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import {
  crmMetaQuery,
  type DealsFilter,
  dealActivityCountersQuery,
  dealsQuery,
  dealsSummaryQuery,
} from '@/lib/crm';
import { formatMoment, formatMoney, plural } from '@/lib/format';
import { assigneesQuery } from '@/lib/queries';

export interface DealsSearch {
  mode?: 'list';
  scope?: 'mine' | 'free';
  direction?: string;
  state?: 'won' | 'lost' | 'all';
  q?: string;
  owner?: string;
  stage?: string;
  source?: string;
  activity?: ActivityFilter;
  createdFrom?: string;
  createdTo?: string;
  closedFrom?: string;
  closedTo?: string;
  amountFrom?: number;
  amountTo?: number;
  sort?: DealSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

const FILTER_KEYS = [
  'scope',
  'direction',
  'state',
  'q',
  'owner',
  'stage',
  'source',
  'activity',
  'createdFrom',
  'createdTo',
  'closedFrom',
  'closedTo',
  'amountFrom',
  'amountTo',
  'sort',
  'dir',
] as const;

/** Условия запроса из адресной строки — те же для списка, счётчиков, выгрузки и группового действия. */
function toFilter(search: DealsSearch, mode: 'board' | 'list'): DealsFilter {
  return {
    scope: search.scope ?? 'all',
    directionId: search.direction,
    q: search.q,
    ownerId: search.owner,
    stageId: search.stage,
    source: search.source,
    activity: search.activity,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    closedFrom: search.closedFrom,
    closedTo: search.closedTo,
    amountFrom: search.amountFrom,
    amountTo: search.amountTo,
    ...(mode === 'board'
      ? { board: true }
      : { state: search.state ?? 'open', page: search.page ?? 1, sort: search.sort, dir: search.dir }),
  };
}

export function DealsPage() {
  const search = useSearch({ strict: false }) as DealsSearch;
  const navigate = useNavigate();
  const meta = useQuery(crmMetaQuery);
  const people = useQuery(assigneesQuery);
  const mode = search.mode ?? 'board';
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const debounce = useRef<number | undefined>(undefined);
  const panelId = useId();
  const cols = useColumns('deals', DEAL_COLUMNS);

  const filter = toFilter(search, mode);
  const list = useQuery(dealsQuery(filter));
  const summary = useQuery({ ...dealsSummaryQuery(filter), enabled: mode === 'board' });
  const activityCounters = useQuery(dealActivityCountersQuery(filter));

  const setSearch = (patch: Partial<DealsSearch>) =>
    navigate({ to: '/crm', search: { ...search, page: undefined, ...patch }, replace: true });
  const current = cleanParams(Object.fromEntries(FILTER_KEYS.map((k) => [k, search[k]])));

  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setSearch({ q: value.trim() || undefined }), 250);
  };

  const panelCount = countActive([
    search.owner,
    search.stage,
    search.source,
    search.createdFrom || search.createdTo,
    search.closedFrom || search.closedTo,
    search.amountFrom !== undefined || search.amountTo !== undefined,
  ]);
  const resetPanel = () =>
    setSearch({
      owner: undefined,
      stage: undefined,
      source: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      closedFrom: undefined,
      closedTo: undefined,
      amountFrom: undefined,
      amountTo: undefined,
    });

  const openDeals = summary.data?.filter(
    (s) => meta.data?.stages.find((x) => x.id === s.stageId)?.semantic === 'open',
  );
  const description = openDeals
    ? `В работе ${openDeals.reduce((s, x) => s + x.count, 0)} ${plural(
        openDeals.reduce((s, x) => s + x.count, 0),
        ['сделка', 'сделки', 'сделок'],
      )} на ${formatMoney(
        openDeals.reduce((s, x) => s + x.amount, 0),
        'RUB',
      )}`
    : list.data && mode === 'list'
      ? `Найдено: ${list.data.total}`
      : undefined;
  const ac = activityCounters.data;
  const amount = (v: string) => (v === '' ? undefined : Math.max(0, Number(v)) || 0);

  return (
    <div>
      <PageHeader
        title="CRM"
        description={description}
        actions={
          <>
            <fieldset className="inline-flex rounded-md border border-line-strong bg-surface p-0.5">
              <legend className="sr-only">Вид</legend>
              {(
                [
                  ['board', 'Канбан', Columns3],
                  ['list', 'Список', List],
                ] as const
              ).map(([m, label, Icon]) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setSearch({ mode: m === 'list' ? 'list' : undefined })}
                  className={clsx(
                    'inline-flex h-8 items-center gap-1.5 rounded px-3 text-[13.5px]',
                    mode === m ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              ))}
            </fieldset>
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Сделка
            </Button>
          </>
        }
      />
      <CrmNav />
      {creating && <DealFormDialog onClose={() => setCreating(false)} />}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Segmented
          label="Чьи сделки"
          value={search.scope ?? 'all'}
          onChange={(v) => setSearch({ scope: v === 'all' ? undefined : v })}
          options={[
            { value: 'all', label: 'Все' },
            { value: 'mine', label: 'Мои' },
            { value: 'free', label: 'Без ответственного' },
          ]}
        />
        <Select
          aria-label="Направление"
          value={search.direction ?? ''}
          onChange={(e) => setSearch({ direction: e.target.value || undefined })}
          className="w-[180px]"
        >
          <option value="">Все направления</option>
          {meta.data?.directions
            .filter((d) => d.isActive)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
        </Select>
        {mode === 'list' && (
          <Select
            aria-label="Состояние"
            value={search.state ?? 'open'}
            onChange={(e) =>
              setSearch({
                state: e.target.value === 'open' ? undefined : (e.target.value as DealsSearch['state']),
              })
            }
            className="w-[150px]"
          >
            <option value="open">В работе</option>
            <option value="won">Успешные</option>
            <option value="lost">Проваленные</option>
            <option value="all">Все</option>
          </Select>
        )}
        <div className="relative w-full max-w-[260px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Сделка, клиент, телефон, №"
            aria-label="Поиск сделок"
            className="pl-9"
          />
        </div>
        <FilterToggle
          open={filterOpen || panelCount > 0}
          active={panelCount}
          controls={panelId}
          onToggle={() => setFilterOpen((v) => !v)}
        />
        <SavedFilters
          page="deals"
          current={current}
          onApply={(p) =>
            navigate({ to: '/crm', search: { mode: search.mode, ...(p as DealsSearch) }, replace: true })
          }
        />
        {mode === 'list' && <ColumnsMenu state={cols} />}
        <ExportButton
          path="/crm/deals/export.xlsx"
          query={{ ...filter, board: undefined, page: undefined }}
        />
      </div>

      <PresetChips
        label="Дела по сделкам"
        value={search.activity}
        onChange={(activity) => setSearch({ activity })}
        options={[
          { value: 'overdue', label: 'Просроченные дела', count: ac?.overdue, tone: 'danger' },
          { value: 'today', label: 'Дела на сегодня', count: ac?.today, tone: 'signal' },
          { value: 'planned', label: 'Запланированные', count: ac?.planned },
          { value: 'none', label: 'Без дел', count: ac?.none },
        ]}
      />

      {(filterOpen || panelCount > 0) && (
        <div className="mt-3">
          <FilterPanel id={panelId} onReset={resetPanel}>
            <FilterField label="Ответственный">
              {(id) => (
                <Select
                  id={id}
                  value={search.owner ?? ''}
                  onChange={(e) => setSearch({ owner: e.target.value || undefined })}
                >
                  <option value="">Любой</option>
                  {people.data?.map((p) => (
                    <option key={p.userId} value={p.userId}>
                      {p.fullName}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <FilterField label="Стадия">
              {(id) => (
                <Select
                  id={id}
                  value={search.stage ?? ''}
                  onChange={(e) => setSearch({ stage: e.target.value || undefined })}
                >
                  <option value="">Любая</option>
                  {meta.data?.stages.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <FilterField label="Источник">
              {(id) => (
                <Select
                  id={id}
                  value={search.source ?? ''}
                  onChange={(e) => setSearch({ source: e.target.value || undefined })}
                >
                  <option value="">Любой</option>
                  {DEAL_SOURCES.map((src) => (
                    <option key={src} value={src}>
                      {DEAL_SOURCE_LABELS[src]}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <fieldset className="min-w-0">
              <legend className="field-label">Сумма, ₽</legend>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  aria-label="Сумма от"
                  placeholder="от"
                  defaultValue={search.amountFrom ?? ''}
                  onBlur={(e) => setSearch({ amountFrom: amount(e.target.value) })}
                  className="min-w-0"
                />
                <span aria-hidden className="text-steel">
                  —
                </span>
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  aria-label="Сумма до"
                  placeholder="до"
                  defaultValue={search.amountTo ?? ''}
                  onBlur={(e) => setSearch({ amountTo: amount(e.target.value) })}
                  className="min-w-0"
                />
              </div>
            </fieldset>
            <DateRange
              label="Создана"
              from={search.createdFrom}
              to={search.createdTo}
              onChange={(createdFrom, createdTo) => setSearch({ createdFrom, createdTo })}
            />
            <DateRange
              label="Закрыта"
              from={search.closedFrom}
              to={search.closedTo}
              onChange={(closedFrom, closedTo) => setSearch({ closedFrom, closedTo })}
            />
          </FilterPanel>
        </div>
      )}

      <div className="mt-4">
        {list.isPending || meta.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : meta.error ? (
          <ErrorBox error={meta.error} />
        ) : mode === 'board' ? (
          <Board
            items={list.data.items}
            stages={meta.data.stages}
            filter={filter}
            summary={summary.data ?? []}
          />
        ) : list.data.items.length === 0 ? (
          <EmptyPanel title="Сделок не найдено">Измените фильтры или создайте сделку.</EmptyPanel>
        ) : (
          <>
            <DealTable
              items={list.data.items}
              sort={{ sort: search.sort, dir: search.dir }}
              onSort={(st) => setSearch({ sort: st.sort, dir: st.sort ? st.dir : undefined })}
              bulk={{
                total: list.data.total,
                filter: { ...filter, page: undefined },
                stages: meta.data.stages,
              }}
              cols={cols}
            />
            <Pager
              page={list.data.page}
              total={list.data.total}
              pageSize={list.data.pageSize}
              onPage={(p) => setSearch({ page: p })}
            />
          </>
        )}
      </div>
    </div>
  );
}

/** «Ближайшее дело»: когда и что; просроченное — красным, без дел — бледно. */
export function NextActivityCell({
  next,
  overdue,
}: {
  next: { dueAt: string; subject: string } | null;
  overdue: boolean;
}) {
  if (!next) return <span className="text-[12.5px] text-steel">нет дел</span>;
  return (
    <span className="block max-w-[220px]">
      <span
        className={clsx(
          'inline-flex items-center gap-1 whitespace-nowrap text-[13px] tabular-nums',
          overdue ? 'font-medium text-danger' : 'text-ink-soft',
        )}
      >
        <CalendarClock className="size-3.5 shrink-0" aria-hidden />
        {formatMoment(next.dueAt)}
      </span>
      <span className="block truncate text-[12.5px] text-steel" title={next.subject}>
        {next.subject}
      </span>
    </span>
  );
}

/** Групповое действие над сделками: ответственный, стадия (в «Отказ» — с причиной), направление. */
function DealBulkActions({
  ids,
  filter,
  stages,
  onDone,
}: {
  ids: string[] | null;
  filter: DealsFilter;
  stages: StageItem[];
  onDone: (r: BulkResult) => void;
}) {
  const qc = useQueryClient();
  const people = useQuery(assigneesQuery);
  const meta = useQuery(crmMetaQuery);
  const [action, setAction] = useState<'owner' | 'stage' | 'direction'>('owner');
  const [owner, setOwner] = useState('');
  const [stage, setStage] = useState('');
  const [reason, setReason] = useState('');
  const [direction, setDirection] = useState('');
  const lost = stages.find((s) => s.id === stage)?.semantic === 'lost';
  const apply = useMutation({
    mutationFn: () =>
      api<BulkResult>('/crm/deals/bulk', {
        method: 'POST',
        body: {
          ...(ids ? { ids } : { filter: cleanParams(filter as Record<string, unknown>) }),
          ...(action === 'owner' && { ownerId: owner === 'none' ? null : owner }),
          ...(action === 'stage' && { stageId: stage, lostReason: lost ? reason : undefined }),
          ...(action === 'direction' && { directionId: direction === 'none' ? null : direction }),
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['crm'] });
      onDone(r);
    },
  });
  const ready =
    action === 'owner'
      ? Boolean(owner)
      : action === 'stage'
        ? Boolean(stage) && (!lost || Boolean(reason.trim()))
        : Boolean(direction);
  return (
    <>
      <Select
        aria-label="Что изменить"
        value={action}
        onChange={(e) => setAction(e.target.value as typeof action)}
        className="w-[225px]"
      >
        <option value="owner">Сменить ответственного</option>
        <option value="stage">Перенести на стадию</option>
        <option value="direction">Поставить направление</option>
      </Select>
      {action === 'owner' && (
        <Select
          aria-label="Новый ответственный"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          className="w-[210px]"
        >
          <option value="">Кому передать…</option>
          <option value="none">Без ответственного</option>
          {people.data?.map((p) => (
            <option key={p.userId} value={p.userId}>
              {p.fullName}
            </option>
          ))}
        </Select>
      )}
      {action === 'stage' && (
        <>
          <Select
            aria-label="Стадия"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="w-[180px]"
          >
            <option value="">Стадия…</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          {lost && (
            <Input
              aria-label="Причина отказа"
              placeholder="Причина отказа"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-[200px]"
            />
          )}
        </>
      )}
      {action === 'direction' && (
        <Select
          aria-label="Направление"
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          className="w-[180px]"
        >
          <option value="">Направление…</option>
          <option value="none">Без направления</option>
          {meta.data?.directions
            .filter((d) => d.isActive)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
        </Select>
      )}
      <Button
        size="sm"
        variant="secondary"
        className="h-8 border-white/40"
        disabled={!ready}
        loading={apply.isPending}
        onClick={() => apply.mutate()}
      >
        Применить
      </Button>
      {apply.error && <span className="text-[#ffb4a8]">{apply.error.message}</span>}
    </>
  );
}

export type DealColumn =
  | 'number'
  | 'stage'
  | 'amount'
  | 'activity'
  | 'owner'
  | 'direction'
  | 'source'
  | 'created'
  | 'closed';
export const DEAL_COLUMNS: ColumnDef<DealColumn>[] = [
  { key: 'number', label: '№' },
  { key: 'stage', label: 'Стадия' },
  { key: 'amount', label: 'Сумма' },
  { key: 'activity', label: 'Ближайшее дело' },
  { key: 'owner', label: 'Ответственный' },
  { key: 'direction', label: 'Направление', defaultHidden: true },
  { key: 'source', label: 'Источник', defaultHidden: true },
  { key: 'created', label: 'Создана' },
  { key: 'closed', label: 'Закрыта', defaultHidden: true },
];

export function DealTable({
  items,
  compact,
  sort,
  onSort,
  bulk,
  cols,
}: {
  items: DealListItem[];
  compact?: boolean;
  sort?: SortState<DealSort>;
  onSort?: (s: SortState<DealSort>) => void;
  /** Выбор строк и групповые действия (только в основном списке) */
  bulk?: { total: number; filter: DealsFilter; stages: StageItem[] };
  /** Видимые колонки (основной список); компактная таблица в карточке клиента — свой набор */
  cols?: ColumnsState<DealColumn>;
}) {
  const show = (k: DealColumn) =>
    cols
      ? cols.show(k)
      : compact
        ? ['number', 'stage', 'amount'].includes(k)
        : !DEAL_COLUMNS.find((c) => c.key === k)?.defaultHidden;
  const sel = useSelection(items.map((d) => d.id));
  const [allMatching, setAllMatching] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const head = (label: string, key: DealSort, align?: 'right') =>
    sort && onSort ? (
      <SortHeader label={label} sortKey={key} state={sort} onSort={onSort} align={align} />
    ) : (
      <th scope="col" className={clsx('px-4 py-2.5 font-medium', align === 'right' && 'text-right')}>
        {label}
      </th>
    );
  const clear = () => {
    sel.clear();
    setAllMatching(false);
  };
  return (
    <>
      {result && <BulkNotice result={result} onClose={() => setResult(null)} />}
      {bulk && sel.count > 0 && (
        <BulkBar
          count={sel.count}
          total={bulk.total}
          allMatching={allMatching}
          onSelectAllMatching={sel.all && bulk.total > sel.count ? () => setAllMatching(true) : undefined}
          onClear={clear}
        >
          <DealBulkActions
            ids={allMatching ? null : [...sel.selected]}
            filter={bulk.filter}
            stages={bulk.stages}
            onDone={(r) => {
              setResult(r);
              clear();
            }}
          />
        </BulkBar>
      )}
      <div className="panel relative overflow-x-auto">
        <table className={clsx('w-full text-left', !compact && 'min-w-[1040px]')}>
          <thead>
            <tr className="border-b border-line text-[13px] text-steel">
              {bulk && (
                <th scope="col" className="w-10 py-2.5 pl-4">
                  <SelectBox
                    checked={sel.all}
                    indeterminate={sel.some}
                    onChange={() => {
                      sel.toggleAll();
                      setAllMatching(false);
                    }}
                    label="Выбрать все сделки на странице"
                  />
                </th>
              )}
              {show('number') && head('№', 'number')}
              {head('Сделка', 'title')}
              {show('stage') && head('Стадия', 'stage')}
              {show('amount') && head('Сумма', 'amount', 'right')}
              {show('activity') && head('Ближайшее дело', 'activity')}
              {show('owner') && head('Ответственный', 'owner')}
              {show('direction') && (
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Направление
                </th>
              )}
              {show('source') && (
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Источник
                </th>
              )}
              {show('created') && head('Создана', 'created')}
              {show('closed') && head('Закрыта', 'closed')}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map((d) => (
              <tr key={d.id} className={clsx('hover:bg-paper/70', sel.has(d.id) && 'bg-signal-soft/40')}>
                {bulk && (
                  <td className="py-2.5 pl-4">
                    <SelectBox
                      checked={sel.has(d.id)}
                      onChange={() => {
                        sel.toggle(d.id);
                        setAllMatching(false);
                      }}
                      label={`Выбрать сделку ${d.number}`}
                    />
                  </td>
                )}
                <td className="px-4 py-2.5 tabular-nums text-steel">{d.number}</td>
                <td className="px-4 py-2.5">
                  <Link to="/crm/deals/$id" params={{ id: d.id }} className="font-medium hover:text-link">
                    {d.title}
                  </Link>
                  {d.unreadMessages > 0 && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 align-middle text-[12.5px] font-medium text-danger"
                      title="Непрочитанные сообщения клиента"
                    >
                      <MessageCircle className="size-3.5" aria-hidden />
                      {d.unreadMessages}
                    </span>
                  )}
                  <span className="block text-[12.5px] text-steel">
                    {[d.contact?.fullName, d.company?.name, d.direction?.name].filter(Boolean).join(' · ')}
                  </span>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <StageBadge stage={d.stage} />
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <Money amount={d.amount} currency={d.currency} />
                </td>
                {!compact && (
                  <>
                    <td className="px-4 py-2.5">
                      {d.stage.semantic === 'open' || d.nextActivity ? (
                        <NextActivityCell next={d.nextActivity} overdue={d.hasOverdueActivity} />
                      ) : (
                        <span className="text-steel">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {d.owner ? (
                        <span className="flex items-center gap-2">
                          <Avatar name={d.owner.fullName} userId={d.owner.userId} size={22} />
                          {d.owner.fullName}
                        </span>
                      ) : (
                        <span className="text-steel">не назначен</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-ink-soft">
                      {formatMoment(d.createdAt)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bulk && (
        <p className="mt-2 text-[12.5px] text-steel">
          Отметьте сделки галочками, чтобы сменить ответственного, стадию или направление сразу у всех.
          «Выбрать все» в заголовке, затем «Выбрать все {bulk.total}» — для всех сделок по фильтру.
        </p>
      )}
    </>
  );
}

// ---------- Канбан ----------

function DealCard({
  deal,
  dragging,
  onDragStart,
  onDragEnd,
}: {
  deal: DealListItem;
  dragging: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  return (
    <Link
      to="/crm/deals/$id"
      params={{ id: deal.id }}
      draggable
      data-deal-id={deal.id}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={clsx(
        'block rounded-md border border-line bg-surface px-3 py-2.5 transition-shadow hover:border-line-strong hover:shadow-[0_4px_14px_-8px_rgb(24_34_43/0.4)]',
        dragging && 'opacity-40',
        !deal.owner && 'border-l-[3px] border-l-signal',
      )}
    >
      <p className="flex gap-2 text-[12px] text-steel">
        <span className="tabular-nums">#{deal.number}</span>
        {deal.direction && <span className="ml-auto truncate">{deal.direction.name}</span>}
      </p>
      <p className="mt-0.5 font-medium leading-snug">{deal.title}</p>
      {(deal.contact || deal.company) && (
        <p className="mt-0.5 truncate text-[13px] text-ink-soft">
          {deal.contact?.fullName ?? deal.company?.name}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-steel">
        <Money amount={deal.amount} currency={deal.currency} className="font-medium text-ink" />
        {deal.nextActivityAt && (
          <span
            className={clsx(
              'inline-flex items-center gap-1',
              deal.hasOverdueActivity && 'font-medium text-danger',
            )}
            title="Ближайшее дело"
          >
            <CalendarClock className="size-3.5" aria-hidden />
            {formatMoment(deal.nextActivityAt)}
          </span>
        )}
        {deal.unreadMessages > 0 && (
          <span
            className="inline-flex items-center gap-1 font-medium text-danger"
            title="Непрочитанные сообщения клиента"
          >
            <MessageCircle className="size-3.5" aria-hidden />
            {deal.unreadMessages}
          </span>
        )}
        {deal.source && deal.source !== 'manual' && !deal.unreadMessages && (
          <span className="truncate">{DEAL_SOURCE_LABELS[deal.source]}</span>
        )}
        <span className="ml-auto">
          {deal.owner ? (
            <span title={`Ответственный: ${deal.owner.fullName}`}>
              <Avatar name={deal.owner.fullName} userId={deal.owner.userId} size={22} />
            </span>
          ) : (
            <span className="text-[12px] font-medium text-[#7a5b00]">нет ответственного</span>
          )}
        </span>
      </div>
    </Link>
  );
}

function Board({
  items,
  stages,
  filter,
  summary,
}: {
  items: DealListItem[];
  stages: StageItem[];
  filter: DealsFilter;
  summary: { stageId: string; count: number; amount: number }[];
}) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [target, setTarget] = useState<{ stageId: string; index: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingLost, setPendingLost] = useState<{
    id: string;
    stageId: string;
    afterId?: string;
    beforeId?: string;
  } | null>(null);
  const key = dealsQuery(filter).queryKey;

  const columns = stages.map((stage) => ({
    stage,
    items: items
      .filter((d) => d.stage.id === stage.id)
      .sort((a, b) => a.sort - b.sort || b.number - a.number),
    sum: summary.find((s) => s.stageId === stage.id),
  }));

  const move = useMutation({
    mutationFn: (v: {
      id: string;
      stageId: string;
      afterId?: string;
      beforeId?: string;
      lostReason?: string;
    }) => api(`/crm/deals/${v.id}/move`, { method: 'POST', body: v }),
    onSuccess: () => setPendingLost(null),
    onError: (err) => {
      setError(err.message);
      window.setTimeout(() => setError(null), 6000);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });

  function indexAt(e: DragEvent, column: HTMLElement) {
    const cards = [...column.querySelectorAll<HTMLElement>('[data-deal-id]')].filter(
      (el) => el.dataset.dealId !== dragId,
    );
    const i = cards.findIndex((el) => {
      const r = el.getBoundingClientRect();
      return e.clientY < r.top + r.height / 2;
    });
    return i === -1 ? cards.length : i;
  }

  function drop(stage: StageItem) {
    if (!dragId || !target) return;
    const column = columns.find((c) => c.stage.id === stage.id)?.items.filter((d) => d.id !== dragId) ?? [];
    const after = column[target.index - 1];
    const before = column[target.index];
    const dragged = items.find((d) => d.id === dragId);
    setDragId(null);
    setTarget(null);
    if (!dragged) return;
    const v = { id: dragged.id, stageId: stage.id, afterId: after?.id, beforeId: before?.id };
    if (stage.semantic === 'lost' && dragged.stage.id !== stage.id) {
      setPendingLost(v);
      return;
    }
    const sort =
      after && before
        ? (after.sort + before.sort) / 2
        : after
          ? after.sort + 1
          : before
            ? before.sort - 1
            : dragged.sort;
    qc.setQueryData(key, (old: Paginated<DealListItem> | undefined) =>
      old ? { ...old, items: old.items.map((d) => (d.id === dragged.id ? { ...d, stage, sort } : d)) } : old,
    );
    move.mutate(v);
  }

  return (
    <div>
      {pendingLost && (
        <LostReasonDialog
          loading={move.isPending}
          onClose={() => setPendingLost(null)}
          onSubmit={(lostReason) => move.mutate({ ...pendingLost, lostReason })}
        />
      )}
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
        >
          {error}
        </div>
      )}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 lg:-mx-8 lg:px-8">
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(250px, 1fr))` }}
        >
          {columns.map((col) => (
            <section
              key={col.stage.id}
              aria-label={col.stage.name}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                setTarget({ stageId: col.stage.id, index: indexAt(e, e.currentTarget) });
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(col.stage);
              }}
              className={clsx(
                'flex min-h-[360px] flex-col rounded-[var(--radius-panel)] bg-sunken/70 p-2',
                target?.stageId === col.stage.id && 'outline-2 outline-dashed outline-line-strong',
              )}
            >
              <header className="px-1.5 pb-2 pt-1">
                <div aria-hidden className="mb-2 h-1 rounded-full" style={{ background: col.stage.color }} />
                <div className="flex items-baseline gap-2">
                  <h2 className="min-w-0 truncate text-[13.5px] font-semibold">{col.stage.name}</h2>
                  <span className="text-[13px] tabular-nums text-steel">
                    {col.sum?.count ?? col.items.length}
                  </span>
                </div>
                <p className="text-[12.5px] tabular-nums text-steel">
                  {formatMoney(col.sum?.amount ?? 0, 'RUB')}
                </p>
              </header>
              <div className="flex flex-col gap-2">
                {col.stage.id === stages[0]?.id && <QuickDeal directionId={filter.directionId} />}
                {(() => {
                  let visibleIndex = 0;
                  return col.items.map((d) => {
                    const lifted = d.id === dragId && target !== null;
                    const i = lifted ? -1 : visibleIndex++;
                    return (
                      <div key={d.id}>
                        {target?.stageId === col.stage.id && target.index === i && (
                          <div aria-hidden className="mb-2 h-1 rounded bg-signal" />
                        )}
                        <DealCard
                          deal={d}
                          dragging={dragId === d.id}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', d.id);
                            setDragId(d.id);
                          }}
                          onDragEnd={() => {
                            setDragId(null);
                            setTarget(null);
                          }}
                        />
                      </div>
                    );
                  });
                })()}
                {target?.stageId === col.stage.id &&
                  dragId &&
                  target.index >= col.items.filter((d) => d.id !== dragId).length && (
                    <div aria-hidden className="h-1 rounded bg-signal" />
                  )}
                {col.items.length === 0 && !target && (
                  <p className="px-1.5 py-6 text-center text-[12.5px] text-steel">Пусто</p>
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[12.5px] text-steel">
        Перетащите карточку в другую колонку, чтобы сменить стадию. В «
        {stages
          .filter((s) => s.semantic !== 'open')
          .map((s) => s.name)
          .join('» и «')}
        » — сделки, закрытые за последние 30 дней. Жёлтая полоса — сделка без ответственного.
      </p>
    </div>
  );
}

/** «Быстрая сделка» одной строкой в первой колонке: клиент по телефону находится или создаётся. */
function QuickDeal({ directionId }: { directionId?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [client, setClient] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api<QuickDealResult>('/crm/deals/quick', {
        method: 'POST',
        body: {
          title,
          clientName: client || null,
          phone: phone || null,
          amount: amount ? Number(amount) : null,
          directionId: directionId ?? null,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['crm'] });
      setNote(
        r.contactCreated
          ? `Сделка #${r.number}, новый клиент`
          : `Сделка #${r.number}, клиент найден по телефону`,
      );
      setTitle('');
      setClient('');
      setPhone('');
      setAmount('');
    },
  });
  if (!open)
    return (
      <>
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setNote(null);
          }}
          className="flex h-9 items-center justify-center gap-1.5 rounded-md border border-dashed border-line-strong text-[13px] text-ink-soft hover:border-steel hover:bg-surface hover:text-ink"
        >
          <Plus className="size-4" aria-hidden />
          Быстрая сделка
        </button>
        {note && <p className="px-1 text-[12.5px] text-ok">{note}</p>}
      </>
    );
  return (
    <form
      className="flex flex-col gap-1.5 rounded-md border border-graphite bg-surface p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <Input
        autoFocus
        aria-label="Название сделки"
        placeholder="Что нужно клиенту"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-8"
      />
      <Input
        aria-label="Клиент"
        placeholder="Имя клиента"
        value={client}
        onChange={(e) => setClient(e.target.value)}
        className="h-8"
      />
      <div className="flex gap-1.5">
        <Input
          aria-label="Телефон"
          placeholder="Телефон"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-8 min-w-0"
        />
        <Input
          aria-label="Сумма, ₽"
          placeholder="Сумма"
          type="number"
          min={0}
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="h-8 w-[90px] shrink-0"
        />
      </div>
      {note && !create.error && <p className="text-[12.5px] text-ok">{note}</p>}
      {create.error && <p className="text-[12.5px] text-danger">{create.error.message}</p>}
      <div className="flex items-center justify-end gap-1.5">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Закрыть
        </Button>
        <Button
          size="sm"
          type="submit"
          loading={create.isPending}
          disabled={!title.trim() || !(client.trim() || phone.trim())}
        >
          Создать
        </Button>
      </div>
    </form>
  );
}
